/**
 * Bar Chart Race View
 * Morphs trail lines into horizontal progress bars - fully canvas-based
 */

import { RACE_ROUTE } from '../config.js';

export class BarChartView {
  constructor(mapController) {
    this.map = mapController;
    this.isActive = false;
    this.animationProgress = 0;
    this.rafId = null;

    // Create overlay container
    this.overlay = document.createElement('div');
    this.overlay.id = 'barchart-overlay';
    this.overlay.className = 'barchart-overlay hidden';
    document.getElementById('app').appendChild(this.overlay);

    // Create close button
    this.closeBtn = document.createElement('button');
    this.closeBtn.className = 'barchart-close';
    this.closeBtn.innerHTML = '×';
    this.closeBtn.title = 'Back to Map (Esc)';
    this.overlay.appendChild(this.closeBtn);

    // Close handler callback
    this.onClose = null;

    // Create canvas for morphing animation
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'morph-canvas';
    this.overlay.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    // Team data
    this.teams = [];
    this.trailPoints = new Map();
    this.barPositions = new Map();

    // Race stats — course length is derived from the data in setTeams(),
    // so it adapts to any race rather than assuming a fixed course.
    this.totalDistance = 0; // nm

    // Bar dimensions (adjusted for mobile)
    this.barHeight = 20;
    this.barGap = 16;
    this.padding = { top: 100, right: 80, bottom: 40, left: 220 };

    // Check for mobile
    this.isMobile = window.innerWidth <= 768;
    if (this.isMobile) {
      this.barHeight = 16;
      this.barGap = 10;
      this.padding = { top: 80, right: 60, bottom: 30, left: 160 };
    }

    this.init();
  }

  init() {
    this.resizeCanvas();

    window.addEventListener('resize', () => {
      this.updateMobileDimensions();
      this.resizeCanvas();
      this.calculateBarPositions();
      if (this.isActive && this.animationProgress === 1) {
        this.drawMorphFrame();
      }
    });

    // Close button handler
    this.closeBtn.addEventListener('click', () => {
      if (this.onClose) this.onClose();
    });

    // Escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isActive && this.onClose) {
        this.onClose();
      }
    });
  }

  setCloseHandler(handler) {
    this.onClose = handler;
  }

  updateMobileDimensions() {
    this.isMobile = window.innerWidth <= 768;
    if (this.isMobile) {
      this.barHeight = 16;
      this.barGap = 10;
      this.padding = { top: 80, right: 60, bottom: 30, left: 160 };
    } else {
      this.barHeight = 20;
      this.barGap = 16;
      this.padding = { top: 100, right: 80, bottom: 40, left: 220 };
    }
  }

  getRequiredHeight() {
    const numTeams = this.teams.length || 40; // Estimate if no teams yet
    const contentHeight = this.padding.top + numTeams * (this.barHeight + this.barGap) + this.padding.bottom;
    return Math.max(window.innerHeight, contentHeight);
  }

  resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    const requiredHeight = this.getRequiredHeight();

    this.canvas.width = window.innerWidth * dpr;
    this.canvas.height = requiredHeight * dpr;
    this.canvas.style.width = `${window.innerWidth}px`;
    this.canvas.style.height = `${requiredHeight}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  setTeams(teams) {
    this.teams = [...teams].sort((a, b) => {
      const aDtf = a.interpolated?.dtf ?? a.latest?.dtf ?? Infinity;
      const bDtf = b.interpolated?.dtf ?? b.latest?.dtf ?? Infinity;
      return aDtf - bDtf;
    });

    // Estimate course length from the data: the largest distance-to-finish
    // ever observed (≈ a boat at the start line) approximates the full course.
    let maxDtf = 0;
    this.teams.forEach(team => {
      (team.positions || []).forEach(p => {
        if (p.dtf > maxDtf) maxDtf = p.dtf;
      });
      const latestDtf = team.latest?.dtf ?? 0;
      if (latestDtf > maxDtf) maxDtf = latestDtf;
    });
    if (maxDtf > 0) this.totalDistance = Math.round(maxDtf);

    // Capture current trail points from map projection
    this.trailPoints.clear();
    this.teams.forEach(team => {
      const positions = team.trailPositions || team.positions || [];
      const projected = positions.map(pos => this.map.project(pos.lng, pos.lat));
      this.trailPoints.set(team.serial, projected);
    });

    // Resize canvas to fit all teams
    this.updateMobileDimensions();
    this.resizeCanvas();
    this.calculateBarPositions();
  }

  calculateBarPositions() {
    const maxBarWidth = window.innerWidth - this.padding.left - this.padding.right;

    this.barPositions.clear();

    this.teams.forEach((team, index) => {
      const total = this.totalDistance || 1;
      const pos = team.interpolated || team.latest;
      const dtf = pos?.dtf ?? total;
      const distanceTraveled = total - dtf;
      const progress = Math.max(0, Math.min(1, distanceTraveled / total));

      const y = this.padding.top + index * (this.barHeight + this.barGap);
      const barWidth = maxBarWidth * progress;

      this.barPositions.set(team.serial, {
        x: this.padding.left,
        y: y,
        width: barWidth,
        maxWidth: maxBarWidth,
        height: this.barHeight,
        progress: progress,
        dtf: dtf,
        name: team.name,
        color: team.color,
        speed: pos?.speed ?? 0,
        rank: index + 1,
      });
    });
  }

  async show() {
    if (this.isActive) return;
    this.isActive = true;

    // Disable map interactions
    this.disableMapInteractions();

    // Scroll to top
    this.overlay.scrollTop = 0;

    this.overlay.classList.remove('hidden');
    await this.animateMorph(0, 1, 1040);
  }

  async hide() {
    if (!this.isActive) return;

    await this.animateMorph(1, 0, 1040);

    this.overlay.classList.add('hidden');
    this.isActive = false;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Re-enable map interactions
    this.enableMapInteractions();
  }

  disableMapInteractions() {
    const mapInstance = this.map.getMap();
    mapInstance.boxZoom.disable();
    mapInstance.scrollZoom.disable();
    mapInstance.dragPan.disable();
    mapInstance.dragRotate.disable();
    mapInstance.keyboard.disable();
    mapInstance.doubleClickZoom.disable();
    mapInstance.touchZoomRotate.disable();
  }

  enableMapInteractions() {
    const mapInstance = this.map.getMap();
    mapInstance.boxZoom.enable();
    mapInstance.scrollZoom.enable();
    mapInstance.dragPan.enable();
    mapInstance.dragRotate.enable();
    mapInstance.keyboard.enable();
    mapInstance.doubleClickZoom.enable();
    mapInstance.touchZoomRotate.enable();
  }

  animateMorph(from, to, duration) {
    return new Promise(resolve => {
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const rawProgress = Math.min(1, elapsed / duration);

        // Easing: ease-in-out cubic
        const eased = rawProgress < 0.5
          ? 4 * rawProgress * rawProgress * rawProgress
          : 1 - Math.pow(-2 * rawProgress + 2, 3) / 2;

        this.animationProgress = from + (to - from) * eased;

        this.drawMorphFrame();

        // Update map opacity - fade out in first 40%, reappear in last 40%
        const mapOpacity = Math.max(0, 1 - this.animationProgress * 2.5);
        document.getElementById('map').style.opacity = mapOpacity;
        document.getElementById('wake-canvas').style.opacity = mapOpacity;
        document.getElementById('boats-canvas').style.opacity = mapOpacity;

        if (rawProgress < 1) {
          this.rafId = requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      this.rafId = requestAnimationFrame(animate);
    });
  }

  drawMorphFrame() {
    const ctx = this.ctx;
    const width = window.innerWidth;
    const height = this.getRequiredHeight();
    const t = this.animationProgress;

    // Fully clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = `rgba(5, 8, 16, ${t * 0.97})`;
    ctx.fillRect(0, 0, width, height);

    if (t === 0) return;

    // Draw header
    if (t > 0.3) {
      const headerAlpha = Math.min(1, (t - 0.3) / 0.4);
      this.drawHeader(ctx, headerAlpha);
    }

    // Draw progress markers
    if (t > 0.5) {
      const markerAlpha = Math.min(1, (t - 0.5) / 0.3);
      this.drawProgressMarkers(ctx, markerAlpha);
    }

    // PASS 1: Draw all bar tracks first
    if (t > 0.7) {
      const trackAlpha = (t - 0.7) / 0.3;
      ctx.fillStyle = `rgba(255, 255, 255, ${0.03 * trackAlpha})`;
      this.teams.forEach(team => {
        const barPos = this.barPositions.get(team.serial);
        if (!barPos) return;
        ctx.beginPath();
        ctx.roundRect(barPos.x, barPos.y, barPos.maxWidth, barPos.height, 4);
        ctx.fill();
      });
    }

    // PASS 2: Draw all morphing trails
    this.teams.forEach(team => {
      const trailPoints = this.trailPoints.get(team.serial);
      const barPos = this.barPositions.get(team.serial);
      if (!trailPoints || trailPoints.length < 2 || !barPos) return;

      const morphedPoints = this.interpolateTrailToBar(trailPoints, barPos, t);
      const lineWidth = 2 + t * (this.barHeight - 2);

      ctx.beginPath();
      ctx.moveTo(morphedPoints[0].x, morphedPoints[0].y);
      for (let i = 1; i < morphedPoints.length; i++) {
        ctx.lineTo(morphedPoints[i].x, morphedPoints[i].y);
      }

      ctx.strokeStyle = team.color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 0.6 + t * 0.4;
      ctx.shadowColor = team.color;
      ctx.shadowBlur = 6 + t * 12;
      ctx.stroke();
    });

    // Reset state after all trails
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    ctx.globalAlpha = 1;

    // PASS 3: Draw all boat indicators
    if (t > 0.5) {
      const boatAlpha = Math.min(1, (t - 0.5) / 0.3);
      this.teams.forEach(team => {
        const trailPoints = this.trailPoints.get(team.serial);
        const barPos = this.barPositions.get(team.serial);
        if (!trailPoints || trailPoints.length < 2 || !barPos) return;

        const morphedPoints = this.interpolateTrailToBar(trailPoints, barPos, t);
        const lastPoint = morphedPoints[morphedPoints.length - 1];

        ctx.beginPath();
        ctx.arc(lastPoint.x, lastPoint.y, 6 + t * 2, 0, Math.PI * 2);
        ctx.fillStyle = team.color;
        ctx.globalAlpha = boatAlpha;
        ctx.shadowColor = team.color;
        ctx.shadowBlur = 10;
        ctx.fill();

        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }

    // Reset all state before labels
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    // PASS 4: Draw all labels LAST (completely separate pass)
    if (t > 0.4) {
      const labelAlpha = Math.min(1, (t - 0.4) / 0.4);
      this.teams.forEach(team => {
        const barPos = this.barPositions.get(team.serial);
        if (!barPos) return;
        this.drawTeamLabel(ctx, barPos, labelAlpha);
      });
    }
  }

  drawHeader(ctx, alpha) {
    ctx.save();
    ctx.globalAlpha = alpha;

    // Title
    ctx.font = '600 24px "Source Serif 4", Georgia, serif';
    ctx.fillStyle = '#e8f4f8';
    ctx.textAlign = 'left';
    ctx.fillText('Race Progress', this.padding.left, 50);

    // Subtitle (course route, if the race defines one)
    if (RACE_ROUTE) {
      ctx.font = '500 12px "JetBrains Mono", monospace';
      ctx.fillStyle = 'rgba(232, 244, 248, 0.5)';
      ctx.fillText(RACE_ROUTE, this.padding.left, 70);
    }

    // Distance
    ctx.textAlign = 'right';
    ctx.fillStyle = '#00d4aa';
    ctx.fillText(`${this.totalDistance} nm`, window.innerWidth - this.padding.right, 50);

    ctx.restore();
  }

  drawProgressMarkers(ctx, alpha) {
    ctx.globalAlpha = alpha * 0.4;
    ctx.font = '500 10px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(232, 244, 248, 0.5)';

    const maxWidth = window.innerWidth - this.padding.left - this.padding.right;

    [0, 25, 50, 75, 100].forEach(pct => {
      const x = this.padding.left + (maxWidth * pct / 100);

      // Vertical line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, this.padding.top - 10);
      ctx.lineTo(x, this.padding.top + this.teams.length * (this.barHeight + this.barGap));
      ctx.stroke();

      // Label
      ctx.fillText(`${pct}%`, x, this.padding.top - 20);
    });

    ctx.globalAlpha = 1;
  }

  drawTeamLabel(ctx, barPos, alpha) {
    ctx.save();

    ctx.globalAlpha = alpha;
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    ctx.textBaseline = 'middle';

    const centerY = barPos.y + barPos.height / 2;

    // Draw rank number (left-aligned at fixed position)
    ctx.font = 'bold 14px "JetBrains Mono", monospace';
    ctx.textAlign = 'left';

    if (barPos.rank <= 3) {
      ctx.fillStyle = '#f5a623';
    } else {
      ctx.fillStyle = 'rgba(232, 244, 248, 0.4)';
    }
    ctx.fillText(`#${barPos.rank}`, 20, centerY);

    // Draw team name (left-aligned, after rank)
    ctx.font = '500 14px "Instrument Sans", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#e8f4f8';
    ctx.fillText(barPos.name.substring(0, 18), 65, centerY);

    // Draw stats on right side
    ctx.textAlign = 'left';
    const statsX = window.innerWidth - this.padding.right + 15;

    ctx.font = '600 13px "JetBrains Mono", monospace';
    ctx.fillStyle = '#e8f4f8';
    ctx.fillText(`${barPos.dtf.toFixed(0)} nm`, statsX, barPos.y + barPos.height / 2 - 6);

    ctx.font = '500 10px "JetBrains Mono", monospace';
    ctx.fillStyle = '#00b4d8';
    ctx.fillText(`${barPos.speed.toFixed(1)} kn`, statsX, barPos.y + barPos.height / 2 + 8);

    ctx.restore();
  }

  interpolateTrailToBar(trailPoints, barPos, t) {
    const result = [];
    const numPoints = Math.max(trailPoints.length, 30);

    for (let i = 0; i < numPoints; i++) {
      const pct = i / (numPoints - 1);

      // Source: position along the trail
      const trailIdx = pct * (trailPoints.length - 1);
      const trailIdxFloor = Math.floor(trailIdx);
      const trailIdxCeil = Math.min(trailPoints.length - 1, trailIdxFloor + 1);
      const trailT = trailIdx - trailIdxFloor;

      const trailX = trailPoints[trailIdxFloor][0] +
        (trailPoints[trailIdxCeil][0] - trailPoints[trailIdxFloor][0]) * trailT;
      const trailY = trailPoints[trailIdxFloor][1] +
        (trailPoints[trailIdxCeil][1] - trailPoints[trailIdxFloor][1]) * trailT;

      // Target: position along the bar
      const barX = barPos.x + pct * barPos.width;
      const barY = barPos.y + barPos.height / 2;

      // Interpolate with easing
      result.push({
        x: trailX + (barX - trailX) * t,
        y: trailY + (barY - trailY) * t,
      });
    }

    return result;
  }

  getIsActive() {
    return this.isActive;
  }

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.overlay.remove();
  }
}
